/* eslint-disable @typescript-eslint/naming-convention */
import { Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { cloneDeep } from 'lodash';
import { AutoComplete, AutoCompleteSelectEvent } from 'primeng/autocomplete';

import { Subject, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { SearchJobsGQL, SearchJobResult } from '../../../generated/graphql.generated';
import { Router } from '@angular/router';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit, OnDestroy {

  @ViewChild(AutoComplete) autoComplete: AutoComplete;

  @Input() disabled = false;

  subs = new SubSink();

  results: SearchJobResult[] = [];
  query = '';
  loading = false;

  private searchTerms = new Subject<string>();

  constructor(
    private searchJobsGQL: SearchJobsGQL,
    private router: Router,
  ) { }

  @HostListener('document:keydown./', [ '$event' ])
  setFocus() {
    const active = document.activeElement;
    const ignoreNodes = [ 'INPUT', 'TEXTAREA' ];

    const isQuillEditor = active.classList.contains('ql-editor');

    const ignore = ignoreNodes.includes(active.nodeName) || isQuillEditor;

    if (ignore) {
      return true;
    }

    this.autoComplete.inputEL.nativeElement.focus();
    this.autoComplete.inputEL.nativeElement.select();
    return false;
  }

  @HostListener('document:keydown.escape', [ '$event' ])
  blur() {

    const el = this.autoComplete.el.nativeElement;

    if (!el) { return; }

    const [ input ] = el.getElementsByTagName('input');

    if (!input) { return; }

    if (input === document.activeElement) {
      input.blur();
    }
  }

  ngOnInit(): void {
    // The outer switchMap operates on the raw (undebounced) term stream, so every
    // new term - or a clear, which pushes '' - immediately cancels whatever inner
    // work (pending debounce or in-flight fetch) belonged to the previous term and
    // resets loading/results right away. debounceTime only delays when the actual
    // fetch for the *current* term is allowed to start.
    this.subs.sink = this.searchTerms.pipe(
      switchMap((search) => {
        this.loading = !!search;
        this.results = [];

        if (!search) {
          return of(null);
        }

        return of(search).pipe(
          debounceTime(300),
          switchMap((term) => this.searchJobsGQL.fetch({ search: term, limit: 10 }, {
            fetchPolicy: 'network-only',
          }).pipe(
            catchError((err) => {
              console.error('Failed to search jobs', err);
              return of(null);
            }),
          )),
        );
      }),
    ).subscribe((res) => {
      this.loading = false;
      this.results = res ? cloneDeep(res.data.searchJobs.results) : [];
    });
  }

  ngOnDestroy() {
      this.subs.unsubscribe();
  }

  search() {
    this.searchTerms.next(this.query);
  }

  select(result: AutoCompleteSelectEvent) {
    const job = result.value as SearchJobResult;

    if (!job?.id) {
      console.error('No id found in result', result);
      return;
    }

    this.query = '';
    this.searchTerms.next('');

    this.router.navigate(['/jobs', 'v3', job.id, 'overview'], {
      queryParams: { zone: job.zoneId },
    });
  }

  onEnter() {
    if (this.query) {
      this.router.navigate(['/search'], { queryParams: { q: this.query } });
      this.query = '';
      this.searchTerms.next('');
    } else {
      this.autoComplete.inputEL.nativeElement.focus();
    }
  }
}